/**
 * Canonical source-of-record rules for manifest data.
 *
 * Three pillars contribute to a manifest:
 *   1. e-Manifest (EPA, highest fidelity)
 *   2. Vendor (CSV import from a hauler)
 *   3. Document (AI-extracted from an uploaded PDF)
 *
 * Display preference is `eManifest -> vendor -> document` so e-Manifest is
 * elevated when present. The one tie-breaker: if both e-Manifest and vendor
 * exist AND a staff user manually edited the vendor data, vendor wins
 * (preserves intentional staff overrides).
 *
 * Everything in this module is pure and derived from the inputs; nothing is
 * persisted. Callers compute these values at read time on every request.
 */

import { WasteManifestSource } from "encamp-prisma/generated/browser";

export { WasteManifestSource };

/**
 * Inputs needed to pick available + primary sources for either a
 * `ProcessedManifest` (uses `vendorManifest.manuallyEdited`) or a
 * `WasteLineUnified` (uses its own `manuallyEdited` column, which means
 * the vendor data was manually edited).
 */
export interface ManifestSourceInputs {
  hasEManifest: boolean;
  hasVendor: boolean;
  hasDocument: boolean;
  /** True when vendor data has been manually edited by a staff user. */
  vendorManuallyEdited?: boolean | null;
}

/**
 * Display priority order. First entry wins when multiple sources are present
 * and `vendorManuallyEdited` is false.
 */
export const MANIFEST_SOURCE_DISPLAY_PRIORITY = [
  WasteManifestSource.eManifest,
  WasteManifestSource.Vendor,
  WasteManifestSource.ManifestDocument,
] as const;

/**
 * Returns which of the three pillars currently have data, in display priority
 * order (eManifest, Vendor, ManifestDocument).
 */
export function getAvailableManifestSources({
  hasEManifest,
  hasVendor,
  hasDocument,
}: ManifestSourceInputs): WasteManifestSource[] {
  const sources: WasteManifestSource[] = [];
  if (hasEManifest) sources.push(WasteManifestSource.eManifest);
  if (hasVendor) sources.push(WasteManifestSource.Vendor);
  if (hasDocument) sources.push(WasteManifestSource.ManifestDocument);
  return sources;
}

/**
 * Pick the primary (display) source according to the rule
 * `eManifest -> vendor -> document`, with `vendorManuallyEdited` keeping
 * vendor when both e-Manifest and vendor exist.
 *
 * Returns `null` only when no source is present (a degenerate state but
 * possible briefly during ingest).
 */
export function getPrimaryManifestSource(inputs: ManifestSourceInputs): WasteManifestSource | null {
  const { hasEManifest, hasVendor, hasDocument, vendorManuallyEdited } = inputs;

  // Tie-breaker: staff manually edited vendor and we have both pillars, vendor wins.
  if (hasVendor && vendorManuallyEdited === true) {
    return WasteManifestSource.Vendor;
  }

  if (hasEManifest) return WasteManifestSource.eManifest;
  if (hasVendor) return WasteManifestSource.Vendor;
  if (hasDocument) return WasteManifestSource.ManifestDocument;
  return null;
}

/**
 * Review state of the source Document behind a `ManifestDocument`-sourced
 * WasteManifest. AI-extracted document data is only treated as a real,
 * surfaceable source once a human has reviewed it.
 */
export interface ManifestDocumentReviewState {
  needsReview?: boolean | null;
  reviewedAt?: Date | string | null;
}

/**
 * Whether a manifest document's parsed data has been human-reviewed and may
 * therefore be surfaced (as a comparable source, in discrepancies, etc.).
 *
 * Fail-closed: a missing DocumentMetadata row counts as NOT reviewed. This is
 * the canonical predicate behind the SQL gate used in the process-manifests
 * pipeline (`COALESCE("needsReview" = false AND "reviewedAt" IS NOT NULL, false)`
 * in createWasteStreams). Keep the two definitions in lockstep.
 */
export function isManifestDocumentReviewed(
  meta: ManifestDocumentReviewState | null | undefined
): boolean {
  return meta != null && meta.needsReview === false && meta.reviewedAt != null;
}

/**
 * Per-source values for one logical field on a manifest line or header.
 * `null` / `undefined` means "this source did not provide a value".
 */
export interface ManifestSourceFieldValues<T> {
  eManifest?: T | null;
  vendor?: T | null;
  document?: T | null;
}

/**
 * Pick the value + source for a single field, given the manifest's
 * primary source.
 *
 * Behavior:
 * - If the primary source has a non-null value, use it.
 * - Otherwise fall back through the display priority order (`eManifest ->
 *   vendor -> document`), skipping the primary we already tried.
 * - When no source has a value, returns `{ value: null, source: null }`.
 *
 * This intentionally does NOT mix sources within a single field (we never
 * report a vendor value labeled as e-Manifest).
 */
export function pickManifestFieldFromPrimary<T>(
  primary: WasteManifestSource | null,
  values: ManifestSourceFieldValues<T>
): { value: T | null; source: WasteManifestSource | null } {
  // Try the primary first.
  if (primary !== null) {
    const primaryValue = readSourceValue(primary, values);
    if (isPresent(primaryValue)) {
      return { value: primaryValue, source: primary };
    }
  }

  // Fall back through the canonical priority order, skipping primary.
  for (const candidate of MANIFEST_SOURCE_DISPLAY_PRIORITY) {
    if (candidate === primary) continue;
    const value = readSourceValue(candidate, values);
    if (isPresent(value)) {
      return { value, source: candidate };
    }
  }

  return { value: null, source: null };
}

function readSourceValue<T>(
  source: WasteManifestSource,
  values: ManifestSourceFieldValues<T>
): T | null | undefined {
  switch (source) {
    case WasteManifestSource.eManifest:
      return values.eManifest;
    case WasteManifestSource.Vendor:
      return values.vendor;
    case WasteManifestSource.ManifestDocument:
      return values.document;
  }
}

function isPresent<T>(value: T | null | undefined): value is T {
  if (value === null || value === undefined) return false;
  // Treat empty strings as absent so a blank source field falls through to a
  // populated alternate source (matches the existing `||` coalesce behavior
  // in `mapWasteLineUnifiedToOutput`).
  if (typeof value === "string" && value.length === 0) return false;
  // Empty arrays count as absent so `federalWasteCodes: []` doesn't shadow a
  // populated array on another source.
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}
